import React from 'react';

import * as Yup from 'yup';

import { emailValidator } from '@common/react/utils/validationHelpers';
import Button from '@common/react/components/Forms/Button';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { BaseParams } from '@common/react/objects/BaseParams';

import '@common/react/scss/components/subscribe.scss';

interface Text {
	subscribe?: string;
	requestSuccess?: string;
	close?: string;
}

interface Props {
	description?: string;
	title?: string;
	className?: string;
	formikField?: any;
	requestName?: string;
	namePlaceholder?: string;
	emailPlaceholder?: string;
	text?: Text;
	getErrorMessage?: (error: string) => React.ReactNode;
	params?: BaseParams;
	buttonClassName?: string;
	withIcon?: boolean;
	withNewsletter?: boolean;
	withName?: boolean;
	withLabel?: boolean;
}

const validationSchema = Yup.object().shape({
	name: Yup.string().required().max(40, { key: 'Must be less than character', value: 40 }),
	email: emailValidator,
});

const validationSchemaWithoutName = Yup.object().shape({
	email: emailValidator,
});

interface FormValues {
	id: number;
	name: string;
	email: string;
	newsletter?: boolean;
}

interface ContactsUsFormState {
	success: 0 | 1 | 2;
	error: null | string;
	loaders: {
		times: boolean;
	};
}

const Subscribe: React.FC<Props> = (props) => {
	const {
		description,
		requestName = 'subscribeToNewsletter',
		title = 'Subscribe to our newsletter',
		className = 'container',
		namePlaceholder = 'Name*',
		emailPlaceholder = 'Email*',
		formikField: FormikFieldProps = FormikField,
		text = {},
		buttonClassName = '',
		getErrorMessage = (error) => (error === 'You have already subscribed'
			? error
			: <>
				Oops!!!
				<br />
				Something went wrong
			</>),
		params,
		withIcon = true,
		withNewsletter = false,
		withName = true,
		withLabel = false,
	} = props;
	const {
		subscribe = 'Subscribe',
		requestSuccess = 'Your request has been sent successfully.\n We will contact you as soon as possible',
		close = 'Close',
	} = text;
	const ref = React.useRef(false);
	const timer = React.useRef<any>(null);

	const [state, setState] = React.useState<ContactsUsFormState>({
		error: null,
		success: 0,
		loaders: {
			times: false,
		},
	});

	const { success } = state;

	const item = {
		id: -1,
		name: '',
		email: '',
		...params,
	};

	return <div className={`subscribe-container ${className}`}>
		<div className="subscribe">
			{withIcon && <div className="subscribe-icon">
				<i className="fa fa-envelope-o" />
			</div>}
			{title && <h2>{title}</h2>}
			{description ? <p className="subscribe__description">
				{description}
			</p> : null}
			<ItemProvider<FormValues>
				id={-1}
				add={item}
				clearForSubmit={({ item }) => ({
					...params,
					...item,
				})}
				loadRequest="subscribeToNewsletter"
				saveRequest={requestName}
				readonly={false}
				validationSchema={withName ? validationSchema : validationSchemaWithoutName}
				resetAfterSave
			>
				<ItemEditor<FormValues>
					showMessages={false}
					beforeSubmit={({ submit }) => {
						submit()
							.catch((message) => {
								setState((state) => ({
									...state,
									isLoading: false,
									error: message,
								}));
								timer.current = setTimeout(() => {
									setState((state) => ({ ...state, error: null }));
								}, 7000);
							});
					}}
					afterSubmit={() => {
						setState((state) => ({
							...state,
							success: 1,
							error: null,
						}));

						ref.current = true;

						setTimeout(() => {
							if (ref.current) {
								setState((state) => ({ ...state, success: 2 }));

								ref.current = false;
							}
						}, 15000);
					}}
					edit={({ state: { loading } }) => (
						<>
							<div className="row">
								{withName && <FormikFieldProps
									fieldName="name"
									title={withLabel ? namePlaceholder : ''}
									inputProps={withLabel ? undefined : { placeholder: namePlaceholder }}
									containerClassName="form-group col-sm-6"
									render={({ form, field }, inputProps?: React.HTMLProps<HTMLInputElement>) =>
										<input
											className="form-control"
											type="text"
											id={field.name}
											{...field}
											{...inputProps}
											onChange={(e) => form.setFieldValue(field.name, e.currentTarget.value?.replace(/\d/g, ''))}
										/>}
								/>}
								<FormikFieldProps
									containerClassName={`form-group col-sm-${withName ? 6 : 12}`}
									fieldName="email"
									title={withLabel ? emailPlaceholder : ''}
									inputProps={{
										inputMode: 'email',
										placeholder: withLabel ? '' : emailPlaceholder,
										id: 'email',
									} as any}
								/>
							</div>
							{withNewsletter && <div className="row">
								<FormikFieldProps
									containerClassName="form-group col-sm-12"
									fieldName="newsletter"
									render={({ form, field }) => <div>
										<label htmlFor="newsletter" className={`checkbox ${field.value ? 'checkbox__checked' : ''}`}>
											<span className="checkbox-inner" />
											<input
												checked={field.value}
												name="newsletter"
												id="newsletter"
												type="checkbox"
												onChange={(e) =>
													form.setFieldValue(field.name, e.currentTarget.checked)}
											/>
											Yes, Subscribe me to newsletter
										</label>
									</div>}
								/>
							</div>}
							<div className="subscribe-submit">
								<Button className={`subscribe-button ${buttonClassName}`} isLoading={loading}>
									{withIcon && <>
										<i className="fa fa-check-circle" />
										{' '}
									</>}
									{subscribe}
								</Button>
							</div>
							<div className={`subscribe__message ${success === 1
								? 'subscribeAnimated subscribeZoomIn' : (success === 2 ? 'subscribeAnimated subscribeZoomOut' : '')}`}
							>
								<div className="subscribe__message-inner">
									<h3>
										{requestSuccess}
									</h3>
									<button
										type="button"
										className="subscribe-button btn btn-default"
										onClick={() => {
											ref.current = false;
											setState((prev) => ({ ...prev, success: 2 }));
										}}
									>
										OK
									</button>
								</div>
							</div>
							{state.error && <div className="subscribe__message subscribeAnimated subscribeAnimated__short subscribeZoomIn">
								<div className="subscribe__message-inner alert alert-danger">
									{getErrorMessage(state.error)}
									<button
										type="button"
										className="subscribe-button btn btn-default"
										onClick={() => {
											setState((prev) => ({ ...prev, error: null }));
											if (timer.current) {
												clearTimeout(timer.current);
												timer.current = null;
											}
										}}
									>
										{close}
									</button>
								</div>
							</div>}
						</>
					)}
				/>
			</ItemProvider>
		</div>
	</div>;
};

export default Subscribe;
